import React from "react";

const DR_TYPE = "deepresearch";

const findUpstreamDeepResearchVariant = (selectedSequence, currentStep) => {
  if (!selectedSequence || !currentStep) return null;
  const steps = selectedSequence.steps || {};
  const cs = parseInt(currentStep, 10);
  if (!cs || isNaN(cs)) return null;
  for (const key of Object.keys(steps)) {
    const k = parseInt(key, 10);
    if (isNaN(k) || k >= cs) continue;
    const variants = steps[key]?.variants || {};
    for (const vk of Object.keys(variants)) {
      const v = variants[vk] || {};
      if ((v.type || "").toLowerCase() === DR_TYPE) return v;
    }
  }
  return null;
};

export const hasUpstreamDeepResearch = (selectedSequence, currentStep) =>
  !!findUpstreamDeepResearchVariant(selectedSequence, currentStep);

/**
 * True if any step (regardless of position) is a Deep Research step.
 * Used to gate the Add Step menu — DR is one-per-sequence because the worker
 * assumes a single research artifact hydrates downstream content.
 */
export const hasDeepResearchStep = (steps) => {
  if (!steps) return false;
  for (const key of Object.keys(steps)) {
    const variants = steps[key]?.variants || {};
    for (const vk of Object.keys(variants)) {
      if ((variants[vk]?.type || "").toLowerCase() === DR_TYPE) return true;
    }
  }
  return false;
};

const EMAIL_TYPES = new Set(["email", "manualemail"]);

/**
 * Should an email step at `currentStep` default to replyAsThread? Only when there's
 * already an email step earlier in the sequence — otherwise we'd flag the whole
 * sequence as "first stage is reply" (which gates contact-add to Gmail-only).
 */
export const hasPriorEmailStep = (steps, currentStep) => {
  if (!steps || !currentStep) return false;
  const cs = parseInt(currentStep, 10);
  if (!cs || isNaN(cs)) return false;
  for (const key of Object.keys(steps)) {
    const k = parseInt(key, 10);
    if (isNaN(k) || k >= cs) continue;
    const variants = steps[key]?.variants || {};
    for (const vk of Object.keys(variants)) {
      const t = (variants[vk]?.type || "").toLowerCase();
      if (EMAIL_TYPES.has(t)) return true;
    }
  }
  return false;
};

export const getReplySubject = (steps, currentStep) => {
  let subject = "";
  for (let k = parseInt(currentStep, 10) - 1; k > 0; k--) {
    const emailVariants = Object.values(steps?.[k]?.variants || {}).filter(v =>
      EMAIL_TYPES.has((v?.type || "").toLowerCase())
    );
    const v = emailVariants.find(e => e.status === "ACTIVE") || emailVariants[0];
    if (v && !v.replyAsThread) {
      subject = v.subject;
      break;
    }
  }
  return `Re: ${subject || "(subject of the earlier email in this thread)"}`;
};

export const getUpstreamDeepResearchConfig = (selectedSequence, currentStep) => {
  const v = findUpstreamDeepResearchVariant(selectedSequence, currentStep);
  if (!v) return null;
  return {
    requireApproval: v.requireApproval !== false,
    objectiveText: v.objectiveText || "",
    sellerProfileId: v.sellerProfileId || ""
  };
};

export const DeepResearchAiBanner = ({
  useAiDefault,
  setUseAiDefault,
  drConfig
}) => {
  const requireApproval = drConfig?.requireApproval !== false;
  if (useAiDefault) {
    const reviewLine = requireApproval
      ? "You'll review and approve each message before it's sent."
      : "Messages send automatically. You can pause anytime from the overview.";
    return (
      <div className="drAiNotice auto">
        <span className="drAiNoticeBadge auto">Auto-generated</span>
        <div className="drAiNoticeContent">
          <div className="drAiNoticeBody">
            Nothing to write here — just save. Content is personalized for each
            contact when they enter the sequence.
          </div>
          <div className="drAiNoticeMeta">{reviewLine}</div>
          <button
            type="button"
            className="drAiNoticeAction"
            onClick={() => setUseAiDefault(false)}
          >
            Write manually
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="drAiNotice custom">
      <span className="drAiNoticeBadge custom">Custom</span>
      <div className="drAiNoticeContent">
        <div className="drAiNoticeBody">
          Your content below will be used for every contact, ignoring the
          per-contact research.
        </div>
        <button
          type="button"
          className="drAiNoticeAction"
          onClick={() => setUseAiDefault(true)}
        >
          Use auto-generated
        </button>
      </div>
    </div>
  );
};

export default DeepResearchAiBanner;
